import { useEffect, useState } from 'react';
import { useDashboardActions } from '../../../lib/useDashboardActions';
import { getPreviousRangeDates, getSameRangeLastYearDates } from '@/shared/lib/dates';
import { CompanyResult } from '../../../model/dashboard.types';
import { MainTypesPlans } from '../../../model/dashboard.types';

export type TrendType = 'previous' | 'lastYear';

export interface CompanyPlanTrendResult {
  opportunity: CompanyResult | null;
  profit: CompanyResult | null;
}

export function useCompanyPlanTrend(
  open: boolean,
  dateStart: string | null,
  dateEnd: string | null,
  trendType: TrendType
) {
  const { getCompanyPlanResult } = useDashboardActions();
  const [trendResult, setTrendResult] = useState<CompanyPlanTrendResult>({ opportunity: null, profit: null });
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);
  const [trendDates, setTrendDates] = useState<{ dateStart: string; dateEnd: string } | null>(null);

  useEffect(() => {
    if (!open || !dateStart || !dateEnd) {
      setTrendResult({ opportunity: null, profit: null });
      setTrendDates(null);
      setTrendError(null);
      return;
    }
    const range =
      trendType === 'previous'
        ? getPreviousRangeDates(dateStart, dateEnd)
        : getSameRangeLastYearDates(dateStart, dateEnd);
    if (!range) {
      setTrendResult({ opportunity: null, profit: null });
      setTrendDates(null);
      return;
    }
    setTrendDates(range);
    setTrendLoading(true);
    setTrendError(null);
    Promise.all([
      getCompanyPlanResult(
        { dateStart: range.dateStart, dateEnd: range.dateEnd, isPeriod: true, type: MainTypesPlans.opportunitie },
        { skipStore: true }
      ),
      getCompanyPlanResult(
        { dateStart: range.dateStart, dateEnd: range.dateEnd, isPeriod: true, type: MainTypesPlans.profit },
        { skipStore: true }
      ),
    ])
      .then(([opp, prof]) => setTrendResult({ opportunity: opp ?? null, profit: prof ?? null }))
      .catch((e: unknown) => {
        setTrendError(e instanceof Error ? e.message : 'Ошибка загрузки прошлого периода');
        setTrendResult({ opportunity: null, profit: null });
      })
      .finally(() => setTrendLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dateStart, dateEnd, trendType]);

  return { trendResult, trendLoading, trendError, trendDates };
}
